import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParticipants, useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useSelector } from 'react-redux';
import { virtualRoomAreaService } from '../services/VirtualRoomAreaService';
import { getPhaserGameSync } from '../PhaserGame';
import type Game from '../scenes/Game';
import type { RootState } from '../stores';
import { resolveConferenceMediaEligibility } from '../components/LiveKitVideoDisplay/utils/resolveConferenceMediaEligibility';

/**
 * LiveKit participant list for avatars/HUD: filtered by conference eligibility
 * (virtual-room co-occupancy, roster, proximity) and password authorization.
 */
export interface FilteredParticipantsResult {
  participants: ReturnType<typeof useParticipants>;
  screenShareTracks: ReturnType<typeof useTracks<[Track.Source.ScreenShare]>>;
  cameraTracks: ReturnType<typeof useTracks<[Track.Source.Camera]>>;
  microphoneTracks: ReturnType<typeof useTracks<[Track.Source.Microphone]>>;
  isParticipantInSameRoom: (participantId: string) => boolean;
}

const SETTLE_GRACE_MS = 2000;

function getFilteredParticipants(
  participants: ReturnType<typeof useParticipants>,
  virtualRoomFilters: Record<string, any>,
  firstSeenMap: Map<string, number>,
  isUnauthorizedInPasswordRoom: boolean
): ReturnType<typeof useParticipants> {
  const phaserGame = getPhaserGameSync();
  const game = phaserGame?.scene?.keys?.game as Game | undefined;
  const myPlayer = game?.myPlayer;

  if (!myPlayer || !game) {
    const localOnly = participants.filter(p => p.isLocal);
    return localOnly.length > 0 ? localOnly : participants;
  }

  const now = Date.now();
  return participants.filter((participant) => {
    if (participant.isLocal) return true;
    if (isUnauthorizedInPasswordRoom) return false;

    if (!firstSeenMap.has(participant.identity)) {
      firstSeenMap.set(participant.identity, now);
    }

    const firstSeen = firstSeenMap.get(participant.identity)!;
    const settleGraceActive = now - firstSeen < SETTLE_GRACE_MS;

    return resolveConferenceMediaEligibility(
      game,
      participant.identity,
      virtualRoomFilters,
      {
        isScreenSharing: participant.isScreenShareEnabled,
        settleGraceActive,
      }
    );
  });
}

export function useVirtualRoomFilteredParticipants(): FilteredParticipantsResult {
  const allParticipants = useParticipants();
  const allScreenShareTracks = useTracks([Track.Source.ScreenShare]);
  const allCameraTracks = useTracks([Track.Source.Camera]);
  const allMicrophoneTracks = useTracks([Track.Source.Microphone]);

  const virtualRoomFilters = useSelector((state: RootState) => state.virtualRoomFilter.filters);

  const firstSeenRef = useRef<Map<string, number>>(new Map());
  const isInitializedRef = useRef(false);

  if (!isInitializedRef.current) {
    const initialSettleTime = Date.now() - SETTLE_GRACE_MS;
    allParticipants.forEach((p) => {
      firstSeenRef.current.set(p.identity, initialSettleTime);
    });
    isInitializedRef.current = true;
  }

  // Pre-calculate initial filtered list synchronously to avoid a flash of all participants on mount
  const phaserGame = getPhaserGameSync();
  const game = phaserGame?.scene?.keys?.game as Game | undefined;
  const myPlayer = game?.myPlayer;
  const myArea = myPlayer ? virtualRoomAreaService.isPlayerInArea(myPlayer.x, myPlayer.y) : null;
  const roomFilter = myArea ? virtualRoomFilters[myArea.roomId] : undefined;
  const mySid = game?.network?.mySessionId || '';
  const myAuthorized = roomFilter?.hasPassword
    ? !!(mySid && roomFilter.authorizedSessionIds.includes(mySid))
    : true;
  const isUnauthorizedInPasswordRoom = !!(roomFilter?.hasPassword && !myAuthorized);

  const initialFiltered = getFilteredParticipants(
    allParticipants,
    virtualRoomFilters,
    firstSeenRef.current,
    isUnauthorizedInPasswordRoom
  );

  const [filteredParticipants, setFilteredParticipants] = useState<ReturnType<typeof useParticipants>>(initialFiltered);

  const allParticipantsRef = useRef(allParticipants);
  allParticipantsRef.current = allParticipants;
  const lastNonEmptyRef = useRef<ReturnType<typeof useParticipants>>(initialFiltered.length > 0 ? initialFiltered : allParticipants);

  useEffect(() => {
    const computeFiltered = () => {
      const participants = allParticipantsRef.current;
      const phaserGame = getPhaserGameSync();
      const game = phaserGame?.scene?.keys?.game as Game | undefined;
      const myPlayer = game?.myPlayer;

      if (!myPlayer || !game) {
        const localOnly = participants.filter(p => p.isLocal);
        setFilteredParticipants(localOnly.length > 0 ? localOnly : participants);
        return;
      }

      const myArea = virtualRoomAreaService.isPlayerInArea(myPlayer.x, myPlayer.y);
      const roomFilter = myArea ? virtualRoomFilters[myArea.roomId] : undefined;
      const mySid = game.network?.mySessionId || '';
      const myAuthorized = roomFilter?.hasPassword
        ? !!(mySid && roomFilter.authorizedSessionIds.includes(mySid))
        : true;
      const isUnauthorizedInPasswordRoom = !!(roomFilter?.hasPassword && !myAuthorized);

      const result = getFilteredParticipants(
        participants,
        virtualRoomFilters,
        firstSeenRef.current,
        isUnauthorizedInPasswordRoom
      );

      const currentIds = new Set(participants.map(p => p.identity));
      firstSeenRef.current.forEach((_, id) => {
        if (!currentIds.has(id)) firstSeenRef.current.delete(id);
      });

      if (result.length === 0 && participants.length > 0 && !isUnauthorizedInPasswordRoom) {
        setFilteredParticipants(prev => {
          if (prev === lastNonEmptyRef.current) return prev;
          return lastNonEmptyRef.current;
        });
      } else {
        if (result.length > 0) lastNonEmptyRef.current = result;
        // Skip state update if the set of participant identities hasn't changed
        setFilteredParticipants(prev => {
          if (
            prev.length === result.length &&
            prev.every((p, i) => p.identity === result[i].identity)
          ) {
            return prev;
          }
          return result;
        });
      }
    };

    computeFiltered();
    const interval = setInterval(computeFiltered, 200);
    return () => clearInterval(interval);
  }, [allParticipants, virtualRoomFilters]);

  const filteredParticipantIds = useMemo(() =>
    new Set(filteredParticipants.map(p => p.identity)),
    [filteredParticipants]
  );

  const filteredScreenShareTracks = useMemo(() => {
    return allScreenShareTracks.filter(track =>
      filteredParticipantIds.has(track.participant.identity)
    );
  }, [allScreenShareTracks, filteredParticipantIds]);

  const filteredCameraTracks = useMemo(() => {
    return allCameraTracks.filter(track =>
      filteredParticipantIds.has(track.participant.identity)
    );
  }, [allCameraTracks, filteredParticipantIds]);

  const filteredMicrophoneTracks = useMemo(() => {
    return allMicrophoneTracks.filter(track =>
      filteredParticipantIds.has(track.participant.identity)
    );
  }, [allMicrophoneTracks, filteredParticipantIds]);

  const isParticipantInSameRoom = useCallback(
    (participantId: string) => filteredParticipantIds.has(participantId),
    [filteredParticipantIds]
  );

  return {
    participants: filteredParticipants,
    screenShareTracks: filteredScreenShareTracks,
    cameraTracks: filteredCameraTracks,
    microphoneTracks: filteredMicrophoneTracks,
    isParticipantInSameRoom,
  };
}
